/**
 * The view of the support inbox, as the address bar holds it. A default is
 * not written. What the inbox is searched for (a person's name) is not
 * part of it: that is personal data, kept out of the address
 * (`@/shared/lib/private-search`).
 */
import type { ThreadsFilter } from "../api/queries";
import { SIDES, type Side } from "../api/types";

function pageOf(value: unknown): number | undefined {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : undefined;
}

/** The tabs: first the threads owed a reply, then every open one, the resolved, all. */
export const INBOX_TABS = ["needs_reply", "open", "resolved", "all"] as const;
export type InboxTab = (typeof INBOX_TABS)[number];
export const DEFAULT_TAB: InboxTab = "needs_reply";

export interface InboxSearch {
  tab?: InboxTab;
  page?: number;
  /** Only the threads written from this app; none: both apps'. */
  side?: Side;
}

export function parseInboxSearch(raw: Readonly<Record<string, unknown>>): InboxSearch {
  const search: InboxSearch = {};
  const tab = INBOX_TABS.find((known) => known === raw["tab"]);
  if (tab !== undefined && tab !== DEFAULT_TAB) search.tab = tab;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  const side = SIDES.find((known) => known === raw["side"]);
  if (side !== undefined) search.side = side;
  return search;
}

export function tabOf(search: InboxSearch): InboxTab {
  return search.tab ?? DEFAULT_TAB;
}

/**
 * What the inbox asks hg-content for. `q` is what it is searched for (part
 * of the owner's name or display id): it comes from the screen, never from
 * the address.
 */
export function threadsFilter(search: InboxSearch, q?: string): ThreadsFilter {
  const filter: ThreadsFilter = {};
  const tab = tabOf(search);
  if (tab !== "all") filter.state = tab;
  if (search.side !== undefined) filter.side = search.side;
  if (q !== undefined) filter.q = q;
  return filter;
}

/** Another tab, from its first page: the app chosen is kept. */
export function withTab(search: InboxSearch, tab: InboxTab): InboxSearch {
  const next: InboxSearch = {};
  if (tab !== DEFAULT_TAB) next.tab = tab;
  if (search.side !== undefined) next.side = search.side;
  return next;
}

/** The view after another app was chosen, or none: the first page of the same tab. */
export function withSide(search: InboxSearch, side: string | undefined): InboxSearch {
  const next: InboxSearch = {};
  if (search.tab !== undefined) next.tab = search.tab;
  const known = SIDES.find((each) => each === side);
  if (known !== undefined) next.side = known;
  return next;
}
